import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html, nothing } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-input-css.js';
import type { PropertyValues } from 'lit';
import type { ValidationMessageMap } from '@zywave/zui-base';

const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;
const DEFAULT_COLOR_VALUE = '#000000';
const SUPPORTS_INPUT_SHOW_PICKER = !!HTMLInputElement.prototype.showPicker;

const VALIDATION_MESSAGE_MAP: ValidationMessageMap = {
  rangeUnderflow: 'min',
  rangeOverflow: 'max',
  stepMismatch: 'step',
  tooShort: 'minlength',
  tooLong: 'maxlength',
  typeMismatch: 'type',
  patternMismatch: 'pattern',
  valueMissing: 'required',
  badInput: 'type',
};

/**
 * A form control to allow a user to provide a value, such as text or color.
 *
 * @element zui-input
 *
 * @slot - An unnamed, optional slot to allow you to provide a native input; if provided, `zui-input` will keep its value in sync with that value. This is helpful for certain server-side applications
 *
 * @event {CustomEvent} input - Modeled after native input events: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/input_event
 * @event {CustomEvent} change - Modeled after native input events: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false]  - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @attr {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @attr {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @attr {string} [validation-message-min] - The message to display when the value is less than the `min` attribute
 * @attr {string} [validation-message-max] - The message to display when the value is greater than the `max` attribute
 * @attr {string} [validation-message-step] - The message to display when the value is not a multiple of the `step` attribute
 * @attr {string} [validation-message-minlength] - The message to display when the length of the value is less than the `minlength` attribute
 * @attr {string} [validation-message-maxlength] - The message to display when the length of the value is greater than the `maxlength` attribute
 * @attr {string} [validation-message-type] - The message to display when the value is not the correct type
 * @attr {string} [validation-message-pattern] - The message to display when the value does not match the `pattern` attribute
 * @attr {string} [validation-message-required] - The message to display when the value is required but not provided
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @prop {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @prop {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @prop {boolean} willValidate - Returns true if the element will be validated when the form is submitted
 * @prop {ValidityState} validity - Returns a {@link https://developer.mozilla.org/en-US/docs/Web/API/ValidityState | ValidityState} object that represents the validity states of an element
 * @prop {string} validationMessage - Returns the error message that would be displayed if the element was to be checked for validity
 *
 * @csspart prefix - (prefix only): style control of the prefix rendered element; this is exposed as a CSS shadow part and can be accessed with `::part(prefix)`
 * @csspart suffix - (suffix only): style control of the suffix rendered element; this is exposed as a CSS shadow part and can be accessed with `::part(suffix)`
 */
export class ZuiInputElement extends ZuiFormAssociatedElement {
  protected get _focusControlSelector() {
    return '.focus-input';
  }

  protected get _formValue() {
    return this.value;
  }

  protected formResetCallback() {
    this.value = '';
  }

  #value = '';
  #internalValue = '';

  /**
   * Input value that is associated with form submission
   */
  @property({ type: String })
  get value(): string {
    let val = this.#parseValue(this.#value, this.type);
    if (this.type === 'color') {
      val = val?.toLowerCase() ?? DEFAULT_COLOR_VALUE;
    }

    return val;
  }

  set value(val) {
    const oldVal = this.#value;
    this.#internalValue = this.#value = this.#parseValue(val, this.type);
    this._setFormValue(this.#value);
    this.requestUpdate('value', oldVal);
  }

  /**
   * An optional placeholder value (e.g. "ghost text"). Use this to provide extra contextual information, but do not use in place of a label!
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Represents that this control must be filled in for form submission
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Represents the maximum length of characters supported by value. Only applicable to text-based inputs (e.g. `text`, `search`)
   */
  @property({ type: Number, attribute: 'maxlength' })
  maxLength = 524288;

  /**
   * Represents the minimum length of characters supported by value. Only applicable to text-based inputs (e.g. `text`, `search`)
   */
  @property({ type: Number, attribute: 'minlength' })
  minLength: number | null = null;

  /**
   * Represents the max value of the input. Only applicable to `number` or `date` inputs. If not set, there is no maximum value.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/max | max}
   */
  @property({ type: String })
  max = '';

  /**
   * Represents the min value of the input. Only applicable to `number` or `date` inputs. If not set, there is no minimum value.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/min | min}
   */
  @property({ type: String })
  min = '';

  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/step | step}
   */
  @property({ type: String })
  step: `${number}` | 'any' | '' = '';

  /**
   * Represents the type of the input
   * @type { 'text' | 'number' | 'color' | 'tel' | 'email' | 'password' | 'url' | 'date' }
   */
  @property({ type: String })
  type: ZuiInputType = 'text';

  /**
   * Represents the pattern that the input's value must match to be valid. Only applicable to text-based inputs (e.g. `text`, `search`)
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/pattern | pattern}
   */
  @property({ type: String })
  pattern = '';

  /**
   * An optional list of values to recommend to the user; best used with `[type='color']`
   */
  @property({ type: Array, attribute: 'recommended-values' })
  recommendedValues: unknown[] | null = null;

  /**
   * (applicable to all types except: `date`, `color`): adds prefixed text within a box, before actual input value
   */
  @property({ type: String })
  prefix: null | string = null;

  /**
   * (applicable to all types except: `date`, `color`): adds suffixed text within a box, after actual input value
   */
  @property({ type: String })
  suffix: null | string = null;

  @queryAssignedElements({ slot: '', flatten: true, selector: 'input' })
  private _slottedInputs?: Array<HTMLInputElement>;

  @query('input')
  private _input?: HTMLInputElement;

  get #slottedInput() {
    return this._slottedInputs?.[0];
  }

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    const type = this.type ?? this.getAttribute('type');
    const value = this.value ?? this.getAttribute('value');
    this._setFormValue(this.#parseValue(value, type));
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);

    if (this.type === 'color' && !this.#value) {
      this.value = DEFAULT_COLOR_VALUE;
    }

    if (this.#slottedInput) {
      this.#slottedInput.addEventListener('change', (e) => e.preventDefault());
    }
  }

  updated(changedProps: PropertyValues<this>) {
    super.updated(changedProps);

    if (this.#slottedInput) {
      for (const k of changedProps.keys()) {
        const observedProperties: (string | number | symbol)[] = ['value', 'readonly', 'disabled'];
        if (observedProperties.includes(k)) {
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          (this.#slottedInput as any)[k] = (this as any)[k];
          if (k === 'value') {
            this.#slottedInput.dispatchEvent(new CustomEvent('input'));
          }
        }
      }
    }

    this.#setValidity();
  }

  render() {
    return html` ${this.#renderInput()} ${this.#renderDatalist()}
      <slot></slot>`;
  }

  #renderInput() {
    const datalistId = this.recommendedValues?.length ? 'recommendedValues' : undefined;

    switch (this.type) {
      case 'color':
        return this.#renderColorInput(datalistId);
      case 'date':
        return this.#renderDateInput();
      default:
        return this.#renderDefaultInput(this.type, {
          maxLength: this.maxLength,
          datalistId,
          minLength: this.minLength,
          min: this.min !== '' ? this.min : undefined,
          max: this.max !== '' ? this.max : undefined,
          step: this.step !== '' ? this.step : undefined,
          pattern: this.pattern !== '' ? this.pattern : undefined,
        });
    }
  }

  #renderColorInput(datalistId?: string) {
    return html`
      <div class="container">
        <input
          class="shadowdom-input ${classMap({ 'has-datalist': !!datalistId })}"
          list="${ifDefined(datalistId)}"
          type="color"
          .value="${this.value}"
          ?disabled="${this.disabled}"
          ?readonly="${this.readOnly}"
          @input="${this.#onInput}"
          @change="${(e: Event) => this.#onChange(e, true)}"
          ?required="${this.required}"
        />
        ${this.#renderDefaultInput('text', {
          maxLength: 7,
          shouldCommitChange() {
            return true;
          },
          shouldCommitInput() {
            return false;
          },
        })}
      </div>
    `;
  }

  #renderDateInput() {
    import('@zywave/zui-icons');
    const classes = {
      'clickable-icon': SUPPORTS_INPUT_SHOW_PICKER,
    };
    return html`
      <div class="date-container">
        ${this.#renderDefaultInput('date', { max: this.max, min: this.min })}
        <zui-icon class="${classMap(classes)}" @click="${this.#showDatePicker}" icon="zui-calendar"></zui-icon>
      </div>
    `;
  }

  #renderDefaultInput(type: ZuiInputType, opts?: DefaultInputOpts) {
    const { datalistId, maxLength, shouldCommitChange, shouldCommitInput, minLength, min, max, step, pattern } =
      opts ?? {};
    const classes = {
      readonly: this.readOnly,
      disabled: this.disabled,
    };
    let { placeholder } = this;
    let value = this.#internalValue;
    if (value === undefined || value === null) {
      value = '';
    }
    if (placeholder === undefined || placeholder === null) {
      placeholder = '';
    }

    return html`
      ${this.prefix ? html`<div part="prefix" class="prefix ${classMap(classes)}">${this.prefix}</div>` : nothing}
      ${this.suffix ? html`<div part="suffix" class="suffix ${classMap(classes)}">${this.suffix}</div>` : nothing}
      <input
        class="shadowdom-input focus-input"
        list="${ifDefined(datalistId)}"
        type="${type}"
        .value="${value}"
        placeholder="${placeholder}"
        ?disabled="${this.disabled}"
        ?readonly="${this.readOnly}"
        @input="${(e: InputEvent) => this.#onInput(e, shouldCommitInput?.(e, this.#internalValue))}"
        @change="${(e: Event) => this.#onChange(e, shouldCommitChange?.(e, this.#internalValue))}"
        ?required="${this.required}"
        maxlength="${ifDefined(maxLength)}"
        minlength="${ifDefined(minLength)}"
        min="${ifDefined(min)}"
        max="${ifDefined(max)}"
        step="${ifDefined(step)}"
        pattern="${ifDefined(pattern)}"
      />
    `;
  }

  #renderDatalist() {
    if (this.recommendedValues?.length) {
      const options = this.recommendedValues.map((x: unknown) => html`<option>${x.toString()}</option>`);
      return html` <datalist id="recommendedValues"> ${options} </datalist>`;
    } else {
      return html``;
    }
  }

  // ATM Chrome & FF differ with input date; clicking icon in Chrome opens a calendar popup and prevents typing a date entry while pop up is open, clicking anywhere on input in FF opens popup but typing an entry is still allowed
  #showDatePicker() {
    if (SUPPORTS_INPUT_SHOW_PICKER) {
      this.renderRoot?.querySelector('input')?.showPicker();
    }
  }

  #onInput(e: InputEvent, commitChange?: boolean) {
    this.#internalValue = (e.target as HTMLInputElement).value;
    if (commitChange !== false) {
      this.value = this.#internalValue;
    }
  }

  #onChange(_e: Event, commitChange?: boolean) {
    if (commitChange !== false) {
      this.value = this.#internalValue;
      this.#setValidity();
    }
    this.dispatchEvent(new CustomEvent('change', { detail: this.value, bubbles: true }));
  }

  #setValidity() {
    const input = this._input;
    if (!input) {
      return;
    }
    const inputValidity = input.validity;
    const validity: ValidityStateFlags = {
      badInput: inputValidity.badInput,
      patternMismatch: inputValidity.patternMismatch,
      rangeOverflow: inputValidity.rangeOverflow,
      rangeUnderflow: inputValidity.rangeUnderflow,
      stepMismatch: inputValidity.stepMismatch,
      customError: this.validity.customError,
      typeMismatch: inputValidity.typeMismatch,
      valueMissing: inputValidity.valueMissing,
      tooShort: input.value && this.minLength !== null && input.value.length < this.minLength,
      tooLong: input.value && this.maxLength !== null && input.value.length > this.maxLength,
    };

    // TODO(pat): some of this behavior re: customError may be worth standardizing in FACE base class, via _setValidity
    // Deferring for now, as there are no other complex implementations
    let message = '';
    // preserve custom error message if it exists
    if (validity.customError) {
      message = this.validationMessage;
    } else {
      message = this._mapValidationMessage(input.validationMessage, validity, VALIDATION_MESSAGE_MAP);
    }

    this._setValidity(validity, message);
  }

  #parseValue(val: string | undefined | null, type: string) {
    if (val === undefined || val === null) {
      val = '';
    }
    let result;
    switch (type) {
      case 'number':
        {
          let v: number | null | undefined | string = Number.parseInt(val);
          if ((Number.isNaN && Number.isNaN(v)) || (window.isNaN && window.isNaN(v))) {
            v = '';
          }
          result = v.toString();
        }
        break;
      case 'color':
        {
          const isValid = HEX_COLOR_REGEX.test(val);
          result = isValid ? val : DEFAULT_COLOR_VALUE;
        }
        break;
      case 'text':
      default:
        result = val.toString();
        break;
    }

    return result;
  }
}

window.customElements.define('zui-input', ZuiInputElement);

interface DefaultInputOpts {
  maxLength?: number;
  minLength?: number;
  datalistId?: string;
  min?: string;
  max?: string;
  step?: string;
  pattern?: string;
  shouldCommitChange?: (event: Event, value: string) => boolean;
  shouldCommitInput?: (event: InputEvent, value: string) => boolean;
}

export type ZuiInputType = 'text' | 'number' | 'color' | 'tel' | 'email' | 'password' | 'url' | 'date';

declare global {
  interface HTMLElementTagNameMap {
    'zui-input': ZuiInputElement;
  }
}
